/**
 * GameScene - Main gameplay scene with modular architecture
 * 
 * REFACTORED VERSION:
 * - Uses modular systems for mechanics (Trampoline, Portal, Wind, etc.)
 * - LevelBuilder handles static level construction
 * - CollisionSystem handles all collision logic
 * - GameScene only orchestrates and manages game state
 * 
 * @module scenes/GameScene
 */

import Phaser from 'phaser';
import Player from '../player/Player';
import Enemy from '../objects/Enemy';
import FlyingEnemy from '../objects/FlyingEnemy';
import ShieldEnemy from '../objects/ShieldEnemy';
import ExplodingEnemy from '../objects/ExplodingEnemy';
import LEVELS, { TOTAL_LEVELS } from '../configs/LevelConfigs';
import { CAMERA, EFFECTS, TIMING } from '../configs/GameConfig';
import eventBus, { GameEvents } from '../core/EventBus';
import serviceLocator, { Services } from '../core/ServiceLocator';

// Systems
import {
    TrampolineSystem,
    PortalSystem,
    WindSystem,
    HazardSystem,
    WaterSystem,
    AsteroidSystem,
    LevelBuilder,
    CollisionSystem
} from '../systems';

export default class GameScene extends Phaser.Scene {
    constructor() {
        super('GameScene');
    }

    init(data) {
        this.currentLevel = data.level || 1;
        this.levelConfig = LEVELS[this.currentLevel - 1];
        this.isGameOver = false;
        this.isRestarting = false;
        this.isTransitioning = false;
        
        // Event unsubscribers for cleanup
        this.eventUnsubscribers = [];
    }

    create() {
        const config = this.levelConfig;

        // Initialize systems
        this.initializeSystems();

        // World setup
        this.matter.world.setBounds(0, 0, config.worldWidth, config.worldHeight, 32, true, true, false, false);
        this.matter.world.setGravity(0, config.gravity || 1);
        this.deathZoneY = config.worldHeight - 50;

        // Build level (platforms, stars, puzzle, goal, decorations)
        this.levelBuilder.build(config);

        // Create player
        this.player = new Player(this, config.playerStart.x, config.playerStart.y);

        // Create enemies (supports typed enemies)
        this.enemies = [];
        config.enemies.forEach(enemyConfig => {
            const enemy = this.createEnemy(enemyConfig);
            if (enemy) this.enemies.push(enemy);
        });

        // State
        this.starsCollected = 0;
        this.doorOpen = false;

        // Create mechanics via systems
        this.createMechanics(config);

        // Setup collision system
        this.collisionSystem.init({
            player: this.player,
            levelBuilder: this.levelBuilder,
            enemies: this.enemies,
            systems: this.systems,
            onPlayerDeath: () => this.playerDeath(),
            onStarCollect: (star) => this.collectStar(),
            onGoalReached: () => this.winLevel(),
            onEnemyKill: (enemy) => this.onEnemyKilled(),
            onButtonPress: () => this.activateButton()
        });

        // Camera setup
        this.setupCamera(config);

        // Audio
        this.initAudio();

        // Launch UI
        this.scene.launch('UIScene', {
            level: this.currentLevel,
            levelName: config.name,
            totalLevels: TOTAL_LEVELS
        });

        // Fade in
        this.cameras.main.fadeIn(EFFECTS.FADE_IN);

        // Input setup
        this.setupInput();

        // Timer tracking
        this.playerHasMoved = false;
        this.lastPlayerX = config.playerStart.x;
        this.lastPlayerY = config.playerStart.y;

        // Subscribe to events
        this.setupEventListeners();
    }

    /**
     * Initialize all game systems
     */
    initializeSystems() {
        this.levelBuilder = new LevelBuilder(this);
        this.collisionSystem = new CollisionSystem(this);
        
        // Mechanic systems
        this.trampolineSystem = new TrampolineSystem(this);
        this.portalSystem = new PortalSystem(this);
        this.windSystem = new WindSystem(this);
        this.hazardSystem = new HazardSystem(this);
        this.waterSystem = new WaterSystem(this);
        this.asteroidSystem = new AsteroidSystem(this);

        // Array of all systems for iteration
        this.systems = [
            this.trampolineSystem,
            this.portalSystem,
            this.windSystem,
            this.hazardSystem,
            this.waterSystem,
            this.asteroidSystem
        ];
    }

    /**
     * Create all mechanic systems from config
     * @param {Object} config - Level configuration
     */
    createMechanics(config) {
        this.trampolineSystem.create(config);
        this.portalSystem.create(config);
        this.windSystem.create(config);
        this.hazardSystem.create(config);
        this.waterSystem.create(config);
        this.asteroidSystem.create(config);
    }

    /**
     * Setup camera with follow and lead effect
     * @param {Object} config - Level configuration
     */
    setupCamera(config) {
        this.cameras.main.setBounds(0, 0, config.worldWidth, config.worldHeight + 200);
        this.cameras.main.startFollow(this.player.sprite, true, CAMERA.LERP_X, CAMERA.LERP_Y);
        this.cameras.main.setZoom(CAMERA.DEFAULT_ZOOM);
        
        // Camera lead (looks ahead of movement)
        this.cameraLeadX = 0;
        this.cameraLeadY = 0;
    }

    /**
     * Initialize audio manager
     */
    initAudio() {
        const audioManager = serviceLocator.get(Services.AUDIO_MANAGER);
        if (audioManager) {
            audioManager.init(this);
            audioManager.playAmbientMusic();
        }
    }

    /**
     * Create an enemy instance based on type configuration
     * @param {Object} config - Enemy config with x, y, type, options
     * @returns {Enemy|FlyingEnemy|ShieldEnemy|ExplodingEnemy} Enemy instance
     */
    createEnemy(config) {
        const { x, y, type, options } = config;

        switch (type) {
            case 'flying':
                return new FlyingEnemy(this, x, y, options);
            case 'shield':
                return new ShieldEnemy(this, x, y, options);
            case 'exploding':
                return new ExplodingEnemy(this, x, y, options);
            default:
                return new Enemy(this, x, y);
        }
    }

    /**
     * Setup keyboard input
     */
    setupInput() {
        this.input.keyboard.on('keydown-R', () => {
            if (!this.isGameOver) {
                this.restartGame();
            }
        });

        this.input.keyboard.on('keydown-M', () => {
            this.goToMenu();
        });
    }

    /**
     * Subscribe to EventBus events with cleanup tracking
     */
    setupEventListeners() {
        // Store unsubscribe functions for cleanup
        this.eventUnsubscribers.push(
            eventBus.on(GameEvents.PLAYER_DEATH, () => this.playerDeath(), this)
        );
        
        this.eventUnsubscribers.push(
            eventBus.on(GameEvents.PLAYER_JUMP, () => {
                // Could add screen effects here
            }, this)
        );
    }

    // ==================== GAME EVENTS ====================

    /**
     * Handle star collection
     */
    collectStar() {
        this.starsCollected++;
        
        // Emit via EventBus (unified event system)
        eventBus.emit(GameEvents.STAR_COLLECTED, { count: this.starsCollected });
    }

    /**
     * Handle enemy killed
     */
    onEnemyKilled() {
        this.enemies = this.enemies.filter(e => {
            const activeSprite = e.sprite || e.physicsSprite;
            return activeSprite && activeSprite.active;
        });
    }

    /**
     * Activate puzzle button
     */
    activateButton() {
        if (this.doorOpen) return;
        
        this.doorOpen = true;
        this.collisionSystem.setDoorOpen(true);

        // Emit button pressed event
        eventBus.emit(GameEvents.BUTTON_PRESSED);

        // Show goal text
        this.levelBuilder.showGoalText();

        // Camera effects
        this.cameras.main.shake(150, 0.008);
        this.tweens.add({
            targets: this.cameras.main,
            zoom: CAMERA.ZOOM_EFFECT_AMOUNT,
            duration: 200,
            yoyo: true
        });

        // Open door animation
        this.levelBuilder.openDoor(this.levelConfig);

        // Emit door opened event
        eventBus.emit(GameEvents.DOOR_OPENED);
    }

    /**
     * Handle player death
     */
    playerDeath() {
        if (this.isGameOver) return;
        this.isGameOver = true;

        // Death particles
        if (this.player && this.player.sprite) {
            this.createDeathParticles(this.player.sprite.x, this.player.sprite.y, 0x0000ff);
            this.player.sprite.setVisible(false);
        }

        // Camera effects
        this.cameras.main.shake(EFFECTS.SHAKE_DURATION.HEAVY, EFFECTS.SHAKE_INTENSITY.HEAVY);
        this.cameras.main.flash(200, 255, 0, 0, false);

        // Emit death event
        eventBus.emit(GameEvents.PLAYER_DEATH);

        // Fade and restart - use global setTimeout to ensure it runs
        this.cameras.main.fadeOut(EFFECTS.DEATH_DELAY, 0, 0, 0);
        
        const level = this.currentLevel;
        setTimeout(() => {
            this.doRestart(level);
        }, EFFECTS.DEATH_DELAY + 100);
    }

    /**
     * Create death particle effect
     */
    createDeathParticles(x, y, color) {
        for (let i = 0; i < EFFECTS.DEATH_PARTICLES; i++) {
            const angle = (i / EFFECTS.DEATH_PARTICLES) * Math.PI * 2;
            const speed = Phaser.Math.Between(50, 100);
            const particle = this.add.rectangle(
                x, y,
                Phaser.Math.Between(6, 14),
                Phaser.Math.Between(6, 14),
                color
            );

            this.tweens.add({
                targets: particle,
                x: x + Math.cos(angle) * speed,
                y: y + Math.sin(angle) * speed + 50,
                alpha: 0,
                rotation: Phaser.Math.Between(-4, 4),
                duration: 700,
                ease: 'Power2',
                onComplete: () => particle.destroy()
            });
        }
    }

    /**
     * Win the level
     */
    winLevel() {
        if (this.isGameOver) return;
        this.isGameOver = true;

        // Get time before any transitions
        const uiScene = this.scene.get('UIScene');
        const finalTime = uiScene ? uiScene.getElapsedTime() : 0;

        // Emit level complete via EventBus
        eventBus.emit(GameEvents.LEVEL_COMPLETE, {
            level: this.currentLevel,
            stars: this.starsCollected
        });

        // Prepare win data
        const winData = {
            won: true,
            level: this.currentLevel,
            levelName: this.levelConfig.name,
            totalLevels: TOTAL_LEVELS,
            starsCollected: this.starsCollected,
            totalStars: this.levelConfig.stars.length,
            timeTaken: finalTime
        };

        // Fade out
        this.cameras.main.fadeOut(EFFECTS.WIN_FADE, 255, 255, 255);

        // Use global setTimeout - independent of Phaser's scene time system
        setTimeout(() => {
            this.doWinTransition(winData);
        }, EFFECTS.WIN_FADE + 100);
    }

    /**
     * Perform win transition
     */
    doWinTransition(winData) {
        this.cleanup();
        this.scene.stop('UIScene');
        this.scene.stop('GameScene');
        this.scene.start('UIScene', winData);
    }

    /**
     * Create sparkle effect
     */
    createSparkle(x, y) {
        const sparkle = this.add.star(x, y, 4, 2, 6, 0xffff00);
        sparkle.setAlpha(0.8);
        this.tweens.add({
            targets: sparkle,
            alpha: 0,
            scale: 2,
            duration: 500,
            onComplete: () => sparkle.destroy()
        });
    }

    /**
     * Restart current level
     */
    restartGame() {
        if (this.isRestarting) return;
        this.isRestarting = true;
        
        this.cameras.main.fadeOut(EFFECTS.FADE_OUT, 255, 0, 0);
        
        const level = this.currentLevel;
        setTimeout(() => {
            this.doRestart(level);
        }, EFFECTS.FADE_OUT + 100);
    }

    /**
     * Perform restart - use scene.start instead of restart for reliability
     */
    doRestart(level) {
        eventBus.emit(GameEvents.LEVEL_RESTART, { level });
        this.cleanup();
        this.scene.stop('UIScene');
        this.scene.stop('GameScene');
        this.scene.start('GameScene', { level });
    }

    /**
     * Go to main menu
     */
    goToMenu() {
        if (this.isTransitioning) return;
        this.isTransitioning = true;
        
        this.cameras.main.fadeOut(EFFECTS.FADE_OUT, 0, 0, 0);
        
        setTimeout(() => {
            this.cleanup();
            this.scene.stop('UIScene');
            this.scene.stop('GameScene');
            this.scene.start('MenuScene');
        }, EFFECTS.FADE_OUT + 100);
    }

    /**
     * Cleanup all resources and event listeners
     */
    cleanup() {
        // Unsubscribe from all EventBus events
        this.eventUnsubscribers.forEach(unsub => {
            if (typeof unsub === 'function') {
                unsub();
            }
        });
        this.eventUnsubscribers = [];

        // Clear EventBus listeners registered with this scene as context
        eventBus.off(GameEvents.PLAYER_DEATH);
        eventBus.off(GameEvents.PLAYER_JUMP);

        // Destroy all systems
        this.systems.forEach(system => system.destroy());
        
        if (this.collisionSystem) {
            this.collisionSystem.destroy();
        }

        // Destroy player
        if (this.player) {
            this.player.destroy();
        }

        // Destroy enemies
        this.enemies.forEach(enemy => {
            if (enemy.destroy) enemy.destroy();
        });
    }

    // ==================== UPDATE LOOP ====================

    update(time, delta) {
        if (this.isGameOver) return;

        // Check void death
        if (this.player && this.player.sprite && this.player.sprite.y > this.deathZoneY) {
            this.playerDeath();
            return;
        }

        // Check box void reset
        this.checkBoxReset();

        // Detect player movement for timer
        this.detectPlayerMovement();

        // Update camera lead effect
        this.updateCameraLead();

        // Update player
        if (this.player) {
            this.player.update();
        }

        // Update enemies (pass player ref for proximity-aware enemies)
        this.enemies.forEach(e => {
            const sprite = e.sprite || e.physicsSprite;
            if (sprite && sprite.active) {
                // ExplodingEnemy needs player reference for detection
                if (e.type === 'exploding') {
                    e.update(this.player);
                } else {
                    e.update();
                }
            }
        });

        // Update systems
        this.windSystem.update(delta, this.player);
        this.asteroidSystem.update(delta);

        // Update moving platforms
        this.levelBuilder.updateMovingPlatforms(delta);

        // Sync tracked bodies (box visuals)
        this.syncTrackedBodies();
    }

    /**
     * Detect if player has started moving (for timer)
     */
    detectPlayerMovement() {
        if (this.playerHasMoved || !this.player || !this.player.sprite) return;

        const currentX = this.player.sprite.x;
        const currentY = this.player.sprite.y;
        
        if (Math.abs(currentX - this.lastPlayerX) > TIMING.MOVE_THRESHOLD || 
            Math.abs(currentY - this.lastPlayerY) > TIMING.MOVE_THRESHOLD) {
            this.playerHasMoved = true;
            eventBus.emit(GameEvents.TIMER_UPDATE, { started: true });
        }
    }

    /**
     * Update camera lead effect
     */
    updateCameraLead() {
        if (!this.player || !this.player.sprite || !this.player.sprite.body) return;

        const velocity = this.player.sprite.body.velocity;
        const targetLeadX = Phaser.Math.Clamp(
            velocity.x * CAMERA.LEAD_X_MULTIPLIER, 
            -CAMERA.LEAD_STRENGTH, 
            CAMERA.LEAD_STRENGTH
        );
        const targetLeadY = Phaser.Math.Clamp(
            velocity.y * CAMERA.LEAD_Y_MULTIPLIER, 
            -CAMERA.LEAD_STRENGTH * 0.5, 
            CAMERA.LEAD_STRENGTH * 0.5
        );
        
        this.cameraLeadX = Phaser.Math.Linear(this.cameraLeadX, targetLeadX, CAMERA.LEAD_LERP);
        this.cameraLeadY = Phaser.Math.Linear(this.cameraLeadY, targetLeadY, CAMERA.LEAD_LERP);
        
        this.cameras.main.setFollowOffset(-this.cameraLeadX, -this.cameraLeadY);
    }

    /**
     * Check if box needs to be reset (fell into void)
     */
    checkBoxReset() {
        const tracked = this.levelBuilder.getTrackedBodies();
        
        tracked.forEach(item => {
            if (!item.body || !item.body.position) return;
            
            const pos = item.body.position;
            
            if (pos.y > this.deathZoneY - 100 && 
                (item.body.label === 'box' || item.body.label === 'magneticBox')) {
                
                const boxOrigin = this.levelBuilder.magneticBoxOriginalPosition || this.levelConfig.box;
                this.matter.body.setPosition(item.body, { x: boxOrigin.x, y: boxOrigin.y });
                this.matter.body.setVelocity(item.body, { x: 0, y: 0 });
                this.matter.body.setAngularVelocity(item.body, 0);
                
                if (item.body.label === 'magneticBox') {
                    item.body.ignoreGravity = true;
                    this.createBoxResetEffect(boxOrigin.x, boxOrigin.y);
                }
            }
        });
    }

    /**
     * Visual effect when box resets
     */
    createBoxResetEffect(x, y) {
        const flash = this.add.circle(x, y, 40, 0xffaa00, 0.6);
        this.tweens.add({
            targets: flash,
            scale: 2,
            alpha: 0,
            duration: 400,
            onComplete: () => flash.destroy()
        });
        
        for (let i = 0; i < 6; i++) {
            const angle = (i / 6) * Math.PI * 2;
            const particle = this.add.circle(x, y, 5, 0x00aaff, 0.8);
            this.tweens.add({
                targets: particle,
                x: x + Math.cos(angle) * 40,
                y: y + Math.sin(angle) * 40,
                alpha: 0,
                duration: 350,
                onComplete: () => particle.destroy()
            });
        }
    }

    /**
     * Sync physics body positions with visuals
     */
    syncTrackedBodies() {
        const tracked = this.levelBuilder.getTrackedBodies();
        
        tracked.forEach(item => {
            if (!item.body || !item.body.position) return;
            
            const pos = item.body.position;
            const angle = item.body.angle || 0;

            item.visuals.forEach(visual => {
                if (visual && visual.x !== undefined) {
                    visual.x = pos.x + item.offsetX;
                    visual.y = pos.y + item.offsetY;
                    if (visual.setRotation) {
                        visual.setRotation(angle);
                    }
                }
            });
        });
    }

    /**
     * Scene shutdown - cleanup
     */
    shutdown() {
        this.cleanup();
    }
}
